import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';

import { AppWrap, MotionWrap } from '../../wrapper';
import { readClient } from '../../client';
import { getSanityImageProps } from '../../utils/sanityImage';
import './Testimonials.scss';

const AUTOPLAY_DELAY = 5000;

export const Testimonials = () => {
  const [brands, setBrands] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPointerPaused, setIsPointerPaused] = useState(false);
  const [isFocusPaused, setIsFocusPaused] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    readClient.fetch('*[_type == "brands"]').then(setBrands);
    readClient.fetch('*[_type == "testimonials"]').then(setTestimonials);
  }, []);

  useEffect(() => {
    setCurrentIndex((index) => (
      testimonials.length === 0 ? 0 : Math.min(index, testimonials.length - 1)
    ));
  }, [testimonials.length]);

  useEffect(() => {
    if (
      testimonials.length <= 1
      || isPointerPaused
      || isFocusPaused
      || shouldReduceMotion
    ) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setCurrentIndex((index) => (index + 1) % testimonials.length);
    }, AUTOPLAY_DELAY);

    return () => window.clearInterval(timer);
  }, [
    currentIndex,
    isFocusPaused,
    isPointerPaused,
    shouldReduceMotion,
    testimonials.length,
  ]);

  const showPrevious = () => {
    setCurrentIndex((index) => (
      index === 0 ? testimonials.length - 1 : index - 1
    ));
  };

  const showNext = () => {
    setCurrentIndex((index) => (index + 1) % testimonials.length);
  };

  const resumeAfterFocusLeaves = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocusPaused(false);
    }
  };

  const testimonial = testimonials[currentIndex];

  return (
    <>
      {testimonial && (
        <>
          <h2 className="head-text">Recommendation</h2>
          <div
            className="app__testimonial-carousel"
            onMouseEnter={() => setIsPointerPaused(true)}
            onMouseLeave={() => setIsPointerPaused(false)}
            onFocusCapture={() => setIsFocusPaused(true)}
            onBlurCapture={resumeAfterFocusLeaves}
          >
            <div className="app__testimonial-item app__flex">
              <img
                {...getSanityImageProps(testimonial.imgurl, {
                  candidates: [
                    { width: 100, height: 100 },
                    { width: 200, height: 200 },
                    { width: 300, height: 300 },
                  ],
                  sizes: '(min-width: 2000px) 150px, 100px',
                })}
                loading="lazy"
                decoding="async"
                alt={`Portrait of ${testimonial.name}`}
              />
              <div className="app__testimonial-content">
                <p className="p-text">{testimonial.feedback}</p>
                <div>
                  <h4 className="bold-test">{testimonial.name}</h4>
                  <h5 className="p-test">{testimonial.company}</h5>
                </div>
              </div>
            </div>

            {testimonials.length > 1 && (
              <div className="app__testimonial-btns app__flex">
                <button
                  type="button"
                  className="app__flex"
                  onClick={showPrevious}
                  aria-label="Show previous recommendation"
                >
                  <HiChevronLeft />
                </button>
                <button
                  type="button"
                  className="app__flex"
                  onClick={showNext}
                  aria-label="Show next recommendation"
                >
                  <HiChevronRight />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {brands.length > 0 && (
        <div className="app__testimonial-brands app__flex">
          {brands.map((brand) => (
            <motion.div
              whileInView={{ opacity: [0, 1] }}
              transition={{ duration: 0.5, type: 'tween' }}
              key={brand._id}
            >
              <img
                {...getSanityImageProps(brand.imgUrl, {
                  candidates: [
                    { width: 120 },
                    { width: 240 },
                    { width: 300 },
                    { width: 420 },
                  ],
                  sizes: '(min-width: 2000px) 210px, (max-width: 450px) 120px, 150px',
                  fit: 'max',
                })}
                loading="lazy"
                decoding="async"
                alt={brand.name}
              />
            </motion.div>
          ))}
        </div>
      )}
    </>
  );
};

export default AppWrap(
  MotionWrap(Testimonials, 'app__testimonial'),
  'testimonials',
  'app__primarygray',
);
